import { useEffect, useRef, useState } from "react";
import { Plug, Unplug, Loader2, RefreshCcw, Delete } from "lucide-react";
import reqEmpresaCredenciais from "../../../../services/reqMultiTenant/reqEmpresaCredenciais";
import {
	getStatus,
	createInstance,
	deleteInstance,
	connectionInstance,
	fetchInstanceByName,
} from "../../../whatsapp-sender/req/reqMessage";
import LibMask from "../../../../util/mask";
import reqWhatsapp from "../../../../services/reqMultiTenant/reqWhatsApp";
import { toast, ToastContainer } from "react-toastify";
import imgOff from "../../../../assets/zapoffline.png";
import imgOn from "../../../../assets/zaponline.png";

const InstanceCard = ({ instanceName, instancePhone, onDelete }) => {
	const [apiKey, setApiKey] = useState("");
	const [url, setUrl] = useState("");
	const [status, setStatus] = useState("LOADING");
	const [loading, setLoading] = useState(false);
	const [qrCode, setQrCode] = useState("");
	const [qrExpired, setQrExpired] = useState(false);
	// Novo estado para saber se QR foi exibido alguma vez (ou “lido”)
	const [qrWasShown, setQrWasShown] = useState(false);

	// Carregar credenciais
	useEffect(() => {
		const fetchCredentials = async () => {
			try {
				if (!instanceName) return;
				const credentials = await reqEmpresaCredenciais.listId("whatsapp");
				setApiKey(credentials.data.api_key || "");
				setUrl(credentials.data.url || "");

				await checkStatus(credentials.data.api_key, credentials.data.url);
			} catch (error) {
				console.error("Erro ao carregar credenciais", error);
			}
		};
		fetchCredentials();
	}, [instanceName, instancePhone]);

	useEffect(() => {
		if (qrWasShown && status === "CONNECTED") {
			// aqui você chama sua função de verificação de instâncias repetidas, por ex:
			verifyAfterConnect();
		}
	}, [status, qrWasShown]);

	const apiKeyRef = useRef(apiKey);
	const urlRef = useRef(url);
	const instanceNameRef = useRef(instanceName);

	useEffect(() => {
		apiKeyRef.current = apiKey;
		urlRef.current = url;
		instanceNameRef.current = instanceName;
	}, [apiKey, url, instanceName]);

	useEffect(() => {
		return () => {
			if (qrWasShown) {
				(async () => {
					const responseWS = await getStatus(urlRef.current, apiKeyRef.current, instanceNameRef.current);

					const status = responseWS?.instance.state?.toUpperCase();

					if (status === "CONNECTED" || status === "OPEN") await verifyAfterConnect();
				})();
			}
		};
	}, [qrWasShown]);

	// Verificar status
	const checkStatus = async (apiKey, url) => {
		if (!apiKey || !url) return;

		try {
			setLoading(true);
			const response = await getStatus(url, apiKey, instanceName);
			const state = (response.instance?.state || "").toUpperCase();

			const statusMap = {
				CONNECTED: "CONNECTED",
				OPEN: "CONNECTED",
				CLOSE: "CONNECTING",
				CONNECTING: "CONNECTING",
				DISCONNECTED: "DISCONNECTED",
				QRCODE: "CONNECTING",
				PAIRING: "CONNECTING",
				STARTING: "CONNECTING",
				TIMEOUT: "DISCONNECTED",
			};

			const newStatus = statusMap[state] || "UNKNOWN";

			setStatus(newStatus);

			if (newStatus === "CONNECTED") {
				setQrCode("");
				setQrExpired(false);
			}
			// if (newStatus === "DISCONNECTED") {
			// 	await connectionInstance(url, apiKey, instanceName);
			// }
		} catch (error) {
			console.log(error);

			const msg = error.message.toLowerCase();

			let fallbackStatus = "UNKNOWN";

			if (msg.includes("não existe")) fallbackStatus = "DISCONNECTED";
			else if (msg.includes("desconectada")) fallbackStatus = "DISCONNECTED";

			setStatus(fallbackStatus);
		} finally {
			setLoading(false);
		}
	};

	// Polling para verificar status
	useEffect(() => {
		if (status === "CONNECTING" || status === "LOADING") {
			const interval = setInterval(() => {
				checkStatus(apiKey, url);
			}, 10000);
			return () => clearInterval(interval);
		}
	}, [status, apiKey, url]);

	const handleReconnect = async () => {
		try {
			setLoading(true);

			await createInstance(url, apiKey, instanceName);
			await checkStatus(apiKey, url);
		} catch (error) {
			console.error("Erro ao conectar", error.message);
		} finally {
			setLoading(false);
		}
	};

	// verifica a instancia e coloca o numero do whatsapp no Banco de dados
	const verifyAfterConnect = async () => {
		try {
			setLoading(true);
			const responseState = await fetchInstanceByName(urlRef.current, apiKeyRef.current, instanceNameRef.current);

			const ownerJid = (responseState[0].ownerJid || "").toUpperCase();

			// const profilePicUrl = (responseState[0].ownerJid || "").toUpperCase();
			const numero = LibMask.onlyPhone(ownerJid);

			const responseInsert = await reqWhatsapp.updateTelefone(instanceNameRef.current, numero);

			return responseState;
		} catch (error) {
			const msg = error.message.toLowerCase();
			// e.preventDefault();

			if (msg.includes("duplicado")) {
				toast.error("Instância com número de telefone duplicado. Conecte com outro número de telefone");
				handleDisconnect();
			}
			console.error("Erro ao conectar", error.message);
		} finally {
			// await checkStatus(apiKey, url);
			setLoading(false);
			setQrWasShown(false);
		}
	};

	const err = (e) => {};

	// Desconectar
	const handleDisconnect = async () => {
		try {
			setLoading(true);
			await deleteInstance(urlRef.current, apiKeyRef.current, instanceNameRef.current);
			const responseInsert = await reqWhatsapp.updateTelefone(instanceNameRef.current, "");

			setStatus("DISCONNECTED");
			setQrCode("");
			setQrWasShown(true);
		} catch (error) {
			console.error("Erro ao desconectar", error.message);
		} finally {
			setLoading(false);
		}
	};

	// Apagar instância
	const handleDelete = async (e) => {
		try {
			e.preventDefault();
			setLoading(true);

			onDelete();
			const responseInsert = await reqWhatsapp.updateDesativar(instanceNameRef.current, "", false);

			// setQrCode("");
			// setQrWasShown(true);
		} catch (error) {
			console.error("Erro ao desconectar", error.message);
		} finally {
			setLoading(false);
		}
	};

	// Gerar QR manualmente
	const handleGenerateQR = async () => {
		try {
			setLoading(true);
			const qr = await connectionInstance(url, apiKey, instanceName);
			setQrCode(qr.base64);
			setQrExpired(false);
			setQrWasShown(true);

			// Expira após 1 min
			setTimeout(() => {
				setQrExpired(true);
				setQrCode("");
			}, 60000);
		} catch (error) {
			console.error("Erro ao gerar QR Code", error.message);
		} finally {
			setLoading(false);
		}
	};

	const statusConfig = {
		CONNECTED: { color: "bg-green-100 text-green-700", text: "Conectado", icon: <Plug size={16} /> },
		CONNECTING: {
			color: "bg-blue-100 text-blue-700",
			text: "Desconectado...",
			icon: <Loader2 size={16} className="animate-spin" />,
		},
		DISCONNECTED: { color: "bg-red-100 text-red-700", text: "Desconectado", icon: <Unplug size={16} /> },
		LOADING: {
			color: "bg-blue-100 text-blue-700",
			text: "Carregando...",
			icon: <Loader2 size={16} className="animate-spin" />,
		},
	};

	const currentStatus = statusConfig[status] || statusConfig.DISCONNECTED;

	return (
		<>
			<ToastContainer />
			<div className="flex flex-col min-w-60  bg-white rounded-xl shadow-md p-2 border hover:shadow-lg transition">
				{/* Cabeçalho */}
				<div className="flex justify-between flex-1">
					<div>
						<h2 className="text-sm font-medium">{instanceName}</h2>
						<h2 className="text-sm ">{instancePhone}</h2>
					</div>
					<div className="flex flex-col ">
						<span
							className={`h-8 flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${currentStatus.color}`}
						>
							{currentStatus.icon}
							{currentStatus.text}
						</span>
					</div>
				</div>

				{/* Botões */}
				{/* Imagem do WhatsApp ou avatar */}
				<div className="flex justify-center my-4">
					<img
						src={status === "CONNECTED" ? imgOn : imgOff}
						alt={status === "CONNECTED" ? "WhatsApp Conectado" : "WhatsApp Desconectado"}
						className="w-20 h-24"
					/>
				</div>

				{status === "CONNECTED" && (
					<button
						onClick={handleDisconnect}
						disabled={loading}
						className={`w-full h-8 flex text-sm items-center justify-center gap-2 bg-red-100 text-red-700 p-2 rounded-md hover:bg-red-200 transition ${
							loading && "opacity-50 cursor-not-allowed"
						}`}
					>
						<Unplug size={16} />
						{loading ? "Desconectando..." : "Desconectar"}
					</button>
				)}

				{status === "DISCONNECTED" && (
					<div className="flex flex-col gap-3">
						<button
							onClick={handleReconnect}
							disabled={loading}
							className={`w-full h-8 flex items-center justify-center gap-2 bg-green-100 text-green-700 py-2 rounded-md hover:bg-green-200 transition ${
								loading && "opacity-50 cursor-not-allowed"
							}`}
						>
							<Plug size={16} />
							{loading ? "Conectando..." : "Criar / Conectar"}
						</button>
						{onDelete && (
							<button
								onClick={handleDelete}
								disabled={loading}
								className={`w-full h-8 flex text-sm items-center justify-center gap-2 bg-red-100 text-red-700 p-2 rounded-md hover:bg-red-200 transition ${
									loading && "opacity-50 cursor-not-allowed"
								}`}
							>
								<Delete size={16} />
								{loading ? "Desconectando..." : "Apagar Instância"}
							</button>
						)}
					</div>
				)}

				{/* QR Code */}
				{qrCode && !qrExpired && (
					<div
						className="flex flex-col"
						// className={`overflow-hidden transition-all duration-300 ease-in-out ${
						// 	qrCode && !qrExpired ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
						// }`}
					>
						<h3 className="text-sm font-medium mb-2">Escaneie o QR Code</h3>
						<img src={qrCode} alt="QR Code" className="mx-auto rounded-md shadow" />
					</div>
				)}

				{(status === "CONNECTING" || status === "CLOSE") && (
					<div className="flex flex-col gap-3">
						<button
							onClick={handleGenerateQR}
							disabled={loading}
							className={`w-full h-8 flex items-center justify-center gap-2 bg-yellow-600 text-white py-2 rounded-lg shadow-md hover:bg-yellow-700 transition`}
							// className={`w-full flex items-center justify-center gap-2 bg-yellow-100 text-yellow-700 py-2 rounded-md hover:bg-yellow-200 transition ${
							// 	loading && "opacity-50 cursor-not-allowed"
							// }`}
						>
							<RefreshCcw size={16} />

							{loading ? "Gerando..." : "Gerar QR Code"}
						</button>
						<button
							onClick={handleDisconnect}
							disabled={loading}
							className={`w-full h-8 flex text-sm items-center justify-center gap-2 bg-red-100 text-red-700 p-2 rounded-md hover:bg-red-200 transition ${
								loading && "opacity-50 cursor-not-allowed"
							}`}
						>
							<Unplug size={16} />
							{loading ? "Desconectando..." : "Cancelar Conexão"}
						</button>
					</div>
				)}
			</div>
		</>
	);
};

export default InstanceCard;

// import React, { useEffect, useState } from "react";
// import { Plug, Unplug, Loader2, RefreshCcw } from "lucide-react";
// import reqEmpresaCredenciais from "../../../../services/reqMultiTenant/reqEmpresaCredenciais";
// import { getStatus, createInstance, deleteInstance, connectionInstance } from "../../../whatsapp-sender/req/reqMessage";

// const InstanceCard = ({ instanceName }) => {
// 	const [apiKey, setApiKey] = useState("");
// 	const [url, setUrl] = useState("");
// 	const [status, setStatus] = useState("LOADING");
// 	const [loading, setLoading] = useState(false);
// 	const [qrCode, setQrCode] = useState("");
// 	const [qrExpired, setQrExpired] = useState(false);

// 	// 🔑 Carregar credenciais
// 	useEffect(() => {
// 		const fetchCredentials = async () => {
// 			try {
// 				const credentials = await reqEmpresaCredenciais.listId("whatsapp");
// 				setApiKey(credentials.data.api_key || "");
// 				setUrl(credentials.data.url || "");
// 				await checkStatus(credentials.data.api_key, credentials.data.url);
// 			} catch (error) {
// 				console.error("Erro ao carregar credenciais", error);
// 			}
// 		};
// 		fetchCredentials();
// 	}, []);

// 	// 🔄 Verificar status
// 	const checkStatus = async (apiKey, url) => {
// 		if (!apiKey || !url) return;

// 		try {
// 			setLoading(true);
// 			const response = await getStatus(url, apiKey, instanceName);
// 			const state = (response.instance?.state || "").toUpperCase();

// 			const statusMap = {
// 				CONNECTED: "CONNECTED",
// 				OPEN: "CONNECTED",
// 				CONNECTING: "CONNECTING",
// 				DISCONNECTED: "DISCONNECTED",
// 				QRCODE: "CONNECTING",
// 				PAIRING: "CONNECTING",
// 				STARTING: "CONNECTING",
// 				TIMEOUT: "DISCONNECTED",
// 			};

// 			const newStatus = statusMap[state] || "UNKNOWN";
// 			setStatus(newStatus);

// 			if (newStatus === "CONNECTED") {
// 				setQrCode("");
// 				setQrExpired(false);
// 			}
// 		} catch (error) {
// 			console.error(`Erro ao verificar status: ${error.message}`);
// 			const msg = error.message.toLowerCase();
// 			let fallbackStatus = "UNKNOWN";

// 			if (msg.includes("não existe")) fallbackStatus = "DISCONNECTED";
// 			else if (msg.includes("desconectada")) fallbackStatus = "DISCONNECTED";

// 			setStatus(fallbackStatus);
// 		} finally {
// 			setLoading(false);
// 		}
// 	};

// 	// 🔁 Polling para verificar status
// 	useEffect(() => {
// 		if (status === "CONNECTING" || status === "LOADING") {
// 			const interval = setInterval(() => {
// 				checkStatus(apiKey, url);
// 			}, 15000);
// 			return () => clearInterval(interval);
// 		}
// 	}, [status, apiKey, url]);

// 	// ➕ Criar instância
// 	const handleReconnect = async () => {
// 		try {
// 			setLoading(true);
// 			await createInstance(url, apiKey, instanceName);
// 			await checkStatus(apiKey, url);
// 		} catch (error) {
// 			console.error("Erro ao conectar", error.message);
// 		} finally {
// 			setLoading(false);
// 		}
// 	};

// 	// ❌ Desconectar
// 	const handleDisconnect = async () => {
// 		if (!window.confirm("Tem certeza que deseja desconectar esta instância?")) return;
// 		try {
// 			setLoading(true);
// 			await deleteInstance(url, apiKey, instanceName);
// 			setStatus("DISCONNECTED");
// 			setQrCode("");
// 		} catch (error) {
// 			console.error("Erro ao desconectar", error.message);
// 		} finally {
// 			setLoading(false);
// 		}
// 	};

// 	// 🔄 Gerar QR manualmente
// 	const handleGenerateQR = async () => {
// 		try {
// 			setLoading(true);
// 			const qr = await connectionInstance(url, apiKey, instanceName);
// 			setQrCode(qr.base64);
// 			setQrExpired(false);

// 			// Expira após 1 min
// 			setTimeout(() => {
// 				setQrExpired(true);
// 				setQrCode("");
// 			}, 10000);
// 		} catch (error) {
// 			console.error("Erro ao gerar QR Code", error.message);
// 		} finally {
// 			setLoading(false);
// 		}
// 	};

// 	const statusConfig = {
// 		CONNECTED: { color: "text-green-600", bg: "bg-green-100", text: "Conectado", icon: <Plug size={18} /> },
// 		CONNECTING: {
// 			color: "text-blue-600",
// 			bg: "bg-blue-100",
// 			text: "",
// 			icon: <Loader2 size={18} className="animate-spin" />,
// 		},
// 		DISCONNECTED: { color: "text-red-600", bg: "bg-red-100", text: "Desconectado", icon: <Unplug size={18} /> },
// 		LOADING: {
// 			color: "text-blue-600",
// 			bg: "bg-blue-100",
// 			text: "Carregando...",
// 			icon: <Loader2 size={18} className="animate-spin" />,
// 		},
// 	};

// 	const currentStatus = statusConfig[status] || statusConfig.DISCONNECTED;

// 	return (
// 		<div className="max-w-sm m-4 bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
// 			{/* Cabeçalho */}
// 			<div className="flex items-center justify-between mb-4">
// 				<h2 className="text-xl font-semibold">{instanceName}</h2>
// 				{loading && (
// 					<span className={`p-2 rounded-full ${currentStatus.bg} ${currentStatus.color}`}>
// 						{currentStatus.icon}
// 					</span>
// 				)}
// 			</div>

// 			{/* Status */}
// 			<p className={`text-lg font-medium ${currentStatus.color}`}>{currentStatus.text}</p>

// 			{/* Botões */}
// 			<div className="mt-6 space-y-3">
// 				{status === "CONNECTED" && (
// 					<button
// 						onClick={handleDisconnect}
// 						disabled={loading}
// 						className={`w-full bg-red-600 text-white py-2 rounded-lg shadow-md hover:bg-red-700 transition ${
// 							loading && "opacity-50 cursor-not-allowed"
// 						}`}
// 					>
// 						{loading ? "Desconectando..." : "Desconectar"}
// 					</button>
// 				)}

// 				{status === "DISCONNECTED" && (
// 					<button
// 						onClick={handleReconnect}
// 						disabled={loading}
// 						className={`w-full bg-green-600 text-white py-2 rounded-lg shadow-md hover:bg-green-700 transition ${
// 							loading && "opacity-50 cursor-not-allowed"
// 						}`}
// 					>
// 						{loading ? "Conectando..." : "Criar / Conectar"}
// 					</button>
// 				)}
// 				{/* QR Code */}
// 				{qrCode && !qrExpired && (
// 					<div className="mt-6 text-center">
// 						<h3 className="text-lg font-semibold mb-2">Escaneie o QR Code</h3>
// 						<img src={qrCode} alt="QR Code" className="mx-auto" />
// 					</div>
// 				)}

// 				{status === "CONNECTING" && (
// 					<button
// 						onClick={handleGenerateQR}
// 						disabled={loading}
// 						className={`w-full flex items-center justify-center gap-2 bg-yellow-600 text-white py-2 rounded-lg shadow-md hover:bg-yellow-700 transition ${
// 							loading && "opacity-50 cursor-not-allowed"
// 						}`}
// 					>
// 						<RefreshCcw size={18} />
// 						{loading ? "Gerando..." : "Gerar QR Code"}
// 					</button>
// 				)}
// 			</div>
// 		</div>
// 	);
// };

// export default InstanceCard;
